import { useEffect, useState } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"

interface UseStartScreenArgs {
  /** The Editor's Tiptap instance */
  editor: TiptapEditor | null
  /** The questionnaire the Editor was opened with (HTML) */
  questionnaire: string
  /** True on Master; the start screen never shows inside a variant */
  inMaster: boolean
  /** False while the collaboration server is connecting or syncing */
  ready: boolean
}

interface UseStartScreen {
  /** Whether the start screen shows now */
  visible: boolean
  /** Closes it for the rest of this visit (Blank, starting the tour) */
  dismiss: () => void
}

/** True when the HTML has no text, e.g. "" or "<p></p>". */
const isBlankHtml = (html: string): boolean =>
  html.replace(/<[^>]*>/g, "").trim() === ""

/**
 * When the empty editor shows its start screen.
 *
 * It shows on Master when the questionnaire was opened empty and the editor
 * is still empty, once the collaboration server is ready (so it never flashes
 * while a questionnaire is still arriving). The first content from anywhere
 * (a tile, typing, a collaborator) closes it for good: deleting everything
 * later does not bring it back until the tab is opened again.
 *
 * @param args - The editor and its state
 * @returns Whether to show it, and how to close it
 */
export const useStartScreen = ({
  editor,
  questionnaire,
  inMaster,
  ready,
}: UseStartScreenArgs): UseStartScreen => {
  const [dismissed, setDismissed] = useState<boolean>(false)
  const [empty, setEmpty] = useState<boolean>(() => editor?.isEmpty ?? true)

  useEffect(() => {
    if (!editor) return
    // Every transaction, not only "update": content set without an update
    // event (initial load, Yjs sync) must close it too.
    const check = (): void => {
      const nowEmpty = editor.isEmpty
      setEmpty(nowEmpty)
      if (!nowEmpty) setDismissed(true)
    }
    check()
    editor.on("transaction", check)
    return () => {
      editor.off("transaction", check)
    }
  }, [editor])

  return {
    visible:
      !dismissed &&
      inMaster &&
      ready &&
      editor !== null &&
      empty &&
      isBlankHtml(questionnaire),
    dismiss: () => setDismissed(true),
  }
}
